/**
 * Error Handling Utility
 * Provides consistent error message formatting across the application
 */

import { ApiError } from '../services/api';

/**
 * Extract a user-friendly error message from various error types
 * @param error - The error object (can be ApiError, Error, or unknown)
 * @param fallbackMessage - Default message if error message cannot be extracted
 * @returns User-friendly error message string
 */
export const getErrorMessage = (
  error: unknown,
  fallbackMessage: string = 'An unexpected error occurred'
): string => {
  // Handle ApiError from our API client
  if (error instanceof ApiError) {
    return error.message;
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Handle objects with message property
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message: unknown }).message;
    if (typeof message === 'string') {
      return message;
    }
  }

  // Fallback for unknown error types
  return fallbackMessage;
};

/**
 * Sanitize error message for safe display in HTML
 * Escapes special characters to prevent XSS attacks
 * @param message - The error message to sanitize
 * @returns Sanitized error message
 */
export const sanitizeErrorForDisplay = (message: string): string => {
  return message
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * Extract and sanitize error message for safe display
 * Combines getErrorMessage() with sanitization
 * @param error - The error object (can be ApiError, Error, or unknown)
 * @param fallbackMessage - Default message if error message cannot be extracted
 * @returns Sanitized user-friendly error message string
 */
export const getSafeErrorMessage = (
  error: unknown,
  fallbackMessage: string = 'An unexpected error occurred'
): string => {
  const message = getErrorMessage(error, fallbackMessage);
  return sanitizeErrorForDisplay(message);
};

/**
 * Log error to console.error (production-appropriate logging)
 * @param context - Description of where/what operation failed
 * @param error - The error object
 */
export const logError = (context: string, error: unknown): void => {
  console.error(`[${context}]`, error);
};
